import * as BABYLON from "babylonjs";
import { Signal } from "./core/signal";
import { AtmosphericScattering } from "./display/atmosphericScattering";
import { EarthCamera } from "./display/earthCamera";
import { EarthMesh } from "./display/earthMesh";
import { SkyMesh } from "./display/skyMesh";
import { CountryService } from "./services/countryService";
import { AnchorManager } from "./ui/anchorManager";
import { InteractionManager } from "./ui/interactionManager";

export class FrameTime {
    constructor(public globalTime: number, public deltaTime: number) {
    }
}

export class EarthEngine {
    private static _instance: EarthEngine | null = null;

    brightness: number = 1.0;
    time = new FrameTime(0, 0);
    canvas: HTMLCanvasElement;
    engine: BABYLON.Engine;
    scene: BABYLON.Scene;
    light: BABYLON.Light;
    camera: EarthCamera;
    earth: EarthMesh;
    sky: SkyMesh;
    atmosphericScattering: AtmosphericScattering;
    anchorManager: AnchorManager;
    interactionManager: InteractionManager;

    didUpdate = new Signal<FrameTime>();
    didRender = new Signal<void>();

    private _currentBrightness: number = 1.0;

    static get instance() {
        if (!EarthEngine._instance) {
            EarthEngine._instance = new EarthEngine();
        }
        return EarthEngine._instance;
    }

    async initialize(canvasElement: string): Promise<void> {
        this.initializeScene(canvasElement);
        await this.initializeServices();
    }

    private initializeScene(canvasElement: string) {
        BABYLON.RenderingManager.MIN_RENDERINGGROUPS = -5;
        BABYLON.RenderingManager.MAX_RENDERINGGROUPS = 5;

        this.canvas = document.getElementById(canvasElement) as HTMLCanvasElement;

        this.engine = new BABYLON.Engine(this.canvas, true);
        this.scene = new BABYLON.Scene(this.engine);
        this.scene.onPointerObservable.add(ev => this.interactionManager.onTap(ev), BABYLON.PointerEventTypes.POINTERTAP);
        this.scene.onPointerObservable.add(ev => this.interactionManager.onDown(ev), BABYLON.PointerEventTypes.POINTERDOWN);

        this.light = new BABYLON.HemisphericLight("MainLight", new BABYLON.Vector3(0, 1, 0), this.scene);
        this.light.specular = new BABYLON.Color3(0.1, 0.1, 0.1);

        this.camera = new EarthCamera(this.scene);
        this.camera.attachControl(this.canvas, false);

        this.earth = new EarthMesh(this.scene);
        this.sky = new SkyMesh(this.scene, "assets/app/textures/skybox");
        this.atmosphericScattering = new AtmosphericScattering(this.scene, this.engine);

        this.anchorManager = new AnchorManager();
        this.interactionManager = new InteractionManager();

        this.scene.onBeforeRenderObservable.add(() => { this.onBeforeRender(); }, undefined, true);
        this.scene.onAfterRenderObservable.add(() => { this.onAfterRender(); });
    }

    private async initializeServices(): Promise<void> {
        await CountryService.instance.loadCountries();
    }

    private updateTime() {
        this.time.deltaTime = Math.min(this.engine.getDeltaTime() * 0.001, 0.05);
        this.time.globalTime += this.time.globalTime;
    }

    private onBeforeRender() {
        this.updateTime();
        this.updateBrightness();
        this.earth.update(this.time);
        this.anchorManager.updateAnchors(this.scene);
        this.didUpdate.emit(this.time);
    }

    private onAfterRender() {
        this.didRender.emit();
    }

    private updateBrightness() {
        if (this._currentBrightness > this.brightness) {
            this._currentBrightness = Math.max(this.brightness, this._currentBrightness - this.time.deltaTime);
        }
        if (this._currentBrightness < this.brightness) {
            this._currentBrightness = Math.min(this.brightness, this._currentBrightness + this.time.deltaTime);
        }

        let factor =  this._currentBrightness;
        let color = new BABYLON.Vector3(factor, factor, factor);
        this.earth.color = color;
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--1!./src/earth/earthEngine.ts